import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Portal,
  Progress,
  Select,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useUser } from '@clerk/clerk-react';
import Button from 'components/ui/Button';
import Dropdown from 'components/ui/Dropdown';
import { useAppointments, useNoShowAppointment, useRescheduleAppointment, useUpdateAppointment } from 'hooks/useAppointments';
import { useProfessionalAvailability, useBatchReplaceAvailability, useCreateAvailability } from 'hooks/useAvailability';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { useUpdateCapacidad } from 'hooks/useUsers';
import { TimeCombobox } from 'components/ui/TimeCombobox';
import AppointmentCancelModal from 'components/layouts/patients/AppointmentCancelModal';
import AppointmentCompleteModal from 'components/layouts/patients/AppointmentCompleteModal';
import AppointmentDrawer from 'components/layouts/patients/AppointmentDrawer';
import AppointmentFormModal from 'components/layouts/patients/AppointmentFormModal';
import AppointmentRescheduleModal from 'components/layouts/patients/AppointmentRescheduleModal';
import CustomModal from 'components/modal/Modal';
import PlanScheduleDrawer from 'components/layouts/scheduling/PlanScheduleDrawer';
import CustomDrawer from 'components/ui/Drawer';
import AvailabilitySettingsCard from 'pages/professional/profile/components/AvailabilitySettingsCard';
import {
  MdAdd,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdEventAvailable,
  MdEventNote,
  MdEventRepeat,
  MdFilterList,
  MdNotes,
  MdPerson,
  MdPersonOff,
  MdSave,
  MdSchedule,
  MdTimer,
  MdToday,
  MdViewDay,
  MdViewModule,
  MdViewWeek,
} from 'react-icons/md';
import { Appointment, EstadoCita, TipoCita } from 'types/models';
import { Availability } from 'services/availabilityService';
import { getUserRole } from 'utils/auth';
import './AgendaView.css';

// ─── Types ────────────────────────────────────────────────────────────────────
type ViewMode = 'week' | 'day' | 'month';
type SlotStatus = 'empty' | 'partial' | 'full' | 'past';

interface SlotBand {
  date: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  topPx: number;
  heightPx: number;
  count: number;
  capacity: number;
  status: SlotStatus;
  isoStart: string;
  durationMinutes: number;
}

const DAY_TO_WEEKDAY: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};
const WEEKDAY_TO_DAY: Record<number, string> = Object.fromEntries(
  Object.entries(DAY_TO_WEEKDAY).map(([id, num]) => [num, id]),
);

function timeMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Asigna columna/total a cada cita que se solapa en el tiempo, para
 * renderizarlas lado a lado en vez de una encima de otra cuando la franja
 * tiene capacidad > 1 (atención en paralelo).
 */
function computeApptColumns(
  appts: Appointment[],
  bands: SlotBand[],
): Record<string, { col: number; total: number }> {
  const sorted = [...appts].sort((a, b) =>
    new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime() || a.id.localeCompare(b.id),
  );

  const range = (appt: Appointment) => {
    const start = new Date(appt.scheduledAt);
    const end   = new Date(start.getTime() + appt.durationMinutes * 60_000);
    return { start, end };
  };

  const layout: Record<string, { col: number; total: number }> = {};

  for (const appt of sorted) {
    const { start, end } = range(appt);

    const band = bands.find(b => {
      const bStart = new Date(`${b.date}T${b.startTime}:00`);
      const bEnd   = new Date(`${b.date}T${b.endTime}:00`);
      return start < bEnd && end > bStart;
    });
    const capacity = band?.capacity ?? 1;

    const usedCols = new Set(
      sorted
        .filter(other => other.id !== appt.id && layout[other.id])
        .filter(other => {
          const o = range(other);
          return start < o.end && end > o.start;
        })
        .map(other => layout[other.id].col),
    );

    let col = 0;
    while (usedCols.has(col)) col++;

    layout[appt.id] = { col, total: Math.max(capacity, col + 1) };
  }

  return layout;
}

function minsToTime(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

function generateSlotBands(
  availability: Availability[],
  appointments: Appointment[],
  days: Date[],
  capacity: number,
  startHour: number,
  hourPx: number,
  gridHeight: number,
): Record<string, SlotBand[]> {
  const now = new Date();
  const byDate: Record<string, SlotBand[]> = {};

  for (const avail of availability) {
    if (!avail.isActive) continue;
    const weekdayIdx = DAY_TO_WEEKDAY[avail.dayOfWeek?.toLowerCase()];
    if (weekdayIdx === undefined) continue;
    const day = days.find(d => d.getDay() === weekdayIdx);
    if (!day) continue;
    const dateKey = toDateKey(day);

    const startM      = timeMins(avail.startTime);
    const endM        = timeMins(avail.endTime);
    const dur         = avail.slotDurationMinutes > 0 ? avail.slotDurationMinutes : 30;
    const slotCapacity = avail.capacidadPorSesion ?? capacity;

    for (let t = startM; t + dur <= endM; t += dur) {
      const slotStart = minsToTime(t);
      const slotEnd   = minsToTime(t + dur);
      const topPx     = (t / 60 - startHour) * hourPx;
      const heightPx  = (dur / 60) * hourPx;

      if (topPx < 0 || topPx >= gridHeight) continue;

      const isoStart      = `${dateKey}T${slotStart}:00`;
      const slotStartDate = new Date(isoStart);
      const slotEndDate   = new Date(`${dateKey}T${slotEnd}:00`);
      const isPast        = slotEndDate <= now;

      const count = isPast ? 0 : appointments.filter(a => {
        if (NON_OCCUPYING_ESTADOS.has(a.estado)) return false;
        if (toDateKey(new Date(a.scheduledAt)) !== dateKey) return false;
        const aStart = new Date(a.scheduledAt);
        const aEnd   = new Date(aStart.getTime() + a.durationMinutes * 60_000);
        return aStart < slotEndDate && aEnd > slotStartDate;
      }).length;

      const status: SlotStatus = isPast
        ? 'past'
        : count === 0 ? 'empty'
        : count < slotCapacity ? 'partial'
        : 'full';

      const pad2 = (n: number) => String(n).padStart(2, '0');
      const d = slotStartDate;
      const isoLocal = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

      if (!byDate[dateKey]) byDate[dateKey] = [];
      byDate[dateKey].push({
        date: dateKey, dayOfWeek: avail.dayOfWeek, startTime: slotStart, endTime: slotEnd,
        topPx, heightPx, count, capacity: slotCapacity, status, isoStart: isoLocal, durationMinutes: dur,
      });
    }
  }
  return byDate;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const START_HOUR  = 0;
const END_HOUR    = 24;
const HOUR_PX     = 96;
const GRID_HEIGHT = (END_HOUR - START_HOUR) * HOUR_PX;
const HOURS       = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);

const DAY_ABBR_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_FULL_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTH_ES    = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

// ─── Styling ──────────────────────────────────────────────────────────────────
const ESTADO_STYLE: Record<EstadoCita, { bg: string; text: string }> = {
  [EstadoCita.CONFIRMADA]:   { bg: '#3B82F6', text: '#FFFFFF' },
  [EstadoCita.COMPLETADA]:   { bg: '#16A34A', text: '#FFFFFF' },
  [EstadoCita.NO_ASISTIO]:   { bg: '#D97706', text: '#FFFFFF' },
  [EstadoCita.CANCELADA]:    { bg: '#DC2626', text: '#FFFFFF' },
  [EstadoCita.REPROGRAMADA]: { bg: '#7C3AED', text: '#FFFFFF' },
};

const TIPO_BADGE: Record<TipoCita, string> = {
  [TipoCita.PRIMERA_VEZ]:   'purple',
  [TipoCita.SEGUIMIENTO]:   'cyan',
  [TipoCita.INTERCONSULTA]: 'orange',
};
const TIPO_LABEL: Record<TipoCita, string> = {
  [TipoCita.PRIMERA_VEZ]:   'Consulta normal',
  [TipoCita.SEGUIMIENTO]:   'Seguimiento',
  [TipoCita.INTERCONSULTA]: 'Interconsulta',
};
const ESTADO_LABEL: Record<EstadoCita, string> = {
  [EstadoCita.CONFIRMADA]:   'Confirmada',
  [EstadoCita.CANCELADA]:    'Cancelada',
  [EstadoCita.COMPLETADA]:   'Completada',
  [EstadoCita.REPROGRAMADA]: 'Reprogramada',
  [EstadoCita.NO_ASISTIO]:   'No asistió',
};
const ESTADO_BADGE: Record<EstadoCita, string> = {
  [EstadoCita.CONFIRMADA]:   'blue',
  [EstadoCita.CANCELADA]:    'red',
  [EstadoCita.COMPLETADA]:   'green',
  [EstadoCita.REPROGRAMADA]: 'purple',
  [EstadoCita.NO_ASISTIO]:   'gray',
};
const FADED_ESTADOS = new Set<EstadoCita>([
  EstadoCita.CANCELADA, EstadoCita.COMPLETADA, EstadoCita.NO_ASISTIO,
]);
// Citas que ya no ocupan cupo del slot: canceladas, no asistidas o movidas
// a otro horario (reprogramada) — el cupo queda libre.
const NON_OCCUPYING_ESTADOS = new Set<EstadoCita>([
  EstadoCita.CANCELADA, EstadoCita.NO_ASISTIO, EstadoCita.REPROGRAMADA,
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toApiDate(date: Date): string {
  return toDateKey(date);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function fmtEndTime(iso: string, dur: number): string {
  const d = new Date(new Date(iso).getTime() + dur * 60_000);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function getTopPx(iso: string): number {
  const d = new Date(iso);
  return Math.max(0, (d.getHours() + d.getMinutes() / 60 - START_HOUR) * HOUR_PX);
}

function getHeightPx(dur: number): number {
  return Math.max(22, (dur / 60) * HOUR_PX);
}

function nowTopPx(): number {
  const d = new Date();
  return (d.getHours() + d.getMinutes() / 60 - START_HOUR) * HOUR_PX;
}

// ─── Popup ────────────────────────────────────────────────────────────────────
interface PopupProps {
  appt: Appointment;
  pos: { x: number; y: number };
  canWrite: boolean;
  isAdmin: boolean;
  currentUserId: string | undefined;
  onClose: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onComplete: () => void;
  onReschedule: () => void;
}

function AppointmentPopup({
  appt, pos, canWrite, isAdmin, currentUserId,
  onClose, onEdit, onCancel, onComplete, onReschedule,
}: PopupProps) {
  const ref    = useRef<HTMLDivElement>(null);
  const toast  = useToast();
  const isConf  = appt.estado === EstadoCita.CONFIRMADA;
  const canComp = isConf && (isAdmin || appt.professionalId === currentUserId);
  const canAct  = isConf && canWrite;

  const [style,         setStyle]         = useState<React.CSSProperties>({ left: pos.x + 12, top: pos.y });
  const [notes,         setNotes]         = useState(appt.notas ?? '');
  const [notesDirty,    setNotesDirty]    = useState(false);
  const [noShowConfirm, setNoShowConfirm] = useState(false);

  const updateAppt = useUpdateAppointment();
  const noShowMut  = useNoShowAppointment();

  useEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect   = ref.current?.getBoundingClientRect();
    const width  = rect?.width  || 300;
    const height = rect?.height || 200;
    let x = pos.x + 12;
    let y = pos.y - 20;
    if (x + width + 12 > vw) x = vw - width - 12;
    if (x < 8) x = 8;
    if (y + height + 12 > vh) y = vh - height - 12;
    if (y < 8) y = 8;
    setStyle({ left: x, top: y });
  }, [pos]);

  const handleSaveNotes = async () => {
    try {
      await updateAppt.mutateAsync({ id: appt.id, payload: { notas: notes } });
      setNotesDirty(false);
      toast({ title: 'Notas guardadas', status: 'success', duration: 2000, isClosable: true, position: 'top-right' });
    } catch {
      toast({ title: 'Error al guardar notas', status: 'error', duration: 3000, isClosable: true, position: 'top-right' });
    }
  };

  const handleNoShow = async () => {
    try {
      await noShowMut.mutateAsync(appt.id);
      toast({
        title: 'No-show registrado',
        description: 'La cita fue marcada como No Asistió',
        status: 'info', duration: 3000, isClosable: true, position: 'top-right',
      });
      onClose();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message ?? 'No se pudo registrar el no-show',
        status: 'error', duration: 4000, isClosable: true, position: 'top-right',
      });
    }
  };

  return (
    <>
      <div className="ag-backdrop" onClick={onClose} />
      <Portal>
        <div ref={ref} className="ag-popup" style={style}>
          {/* Header */}
          <div className="ag-popup-header">
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="ag-popup-title">
                {appt.patient.nombres} {appt.patient.apellidos}
              </p>
              <div className="ag-popup-meta">
                <Badge colorScheme={TIPO_BADGE[appt.tipoCita]} fontSize="10px" borderRadius="full" px="7px">
                  {TIPO_LABEL[appt.tipoCita]}
                </Badge>
                <Badge colorScheme={ESTADO_BADGE[appt.estado]} variant="subtle" fontSize="10px" borderRadius="full" px="7px">
                  {ESTADO_LABEL[appt.estado]}
                </Badge>
              </div>
            </div>
            <button className="ag-popup-close" onClick={onClose} aria-label="Cerrar">
              <Icon as={MdClose} w="16px" h="16px" />
            </button>
          </div>

          {/* Info rows */}
          <div className="ag-popup-body">
            <div className="ag-popup-row">
              <Icon as={MdSchedule} className="ag-popup-row-icon" w="14px" h="14px" />
              <span>{fmtTime(appt.scheduledAt)} – {fmtEndTime(appt.scheduledAt, appt.durationMinutes)}</span>
            </div>
            <div className="ag-popup-row">
              <Icon as={MdTimer} className="ag-popup-row-icon" w="14px" h="14px" />
              <span>{appt.durationMinutes} min</span>
            </div>
            <div className="ag-popup-row">
              <Icon as={MdPerson} className="ag-popup-row-icon" w="14px" h="14px" />
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{appt.patient.cedula}</span>
            </div>
            {appt.motivo && (
              <div className="ag-popup-row" style={{ color: '#64748B', fontSize: 12 }}>
                <span style={{ paddingLeft: 24 }}>{appt.motivo}</span>
              </div>
            )}
          </div>

          {/* Notes section */}
          <div className="ag-popup-notes">
            <div className="ag-popup-notes-label">
              <Icon as={MdNotes} w="12px" h="12px" />
              <span>Notas de la cita</span>
            </div>
            <textarea
              className="ag-popup-notes-input"
              value={notes}
              onChange={e => { setNotes(e.target.value); setNotesDirty(true); }}
              placeholder="Añadir notas internas..."
              rows={2}
              disabled={!canWrite}
            />
            {notesDirty && (
              <button
                className="ag-popup-notes-save"
                onClick={handleSaveNotes}
                disabled={updateAppt.isPending}
              >
                {updateAppt.isPending ? 'Guardando...' : 'Guardar notas'}
              </button>
            )}
          </div>

          {/* Actions */}
          {(canAct || canComp) && (
            <div className="ag-popup-actions">
              {!noShowConfirm ? (
                <>
                  {canAct && (
                    <Button size="xs" variant="outline" onClick={onEdit}>Editar</Button>
                  )}
                  {canAct && (
                    <Button
                      size="xs"
                      colorScheme="purple"
                      variant="outline"
                      leftIcon={<Icon as={MdEventRepeat} w="11px" h="11px" />}
                      onClick={onReschedule}
                    >
                      Reagendar
                    </Button>
                  )}
                  {canComp && (
                    <Button size="xs" colorScheme="green" onClick={onComplete}>Completar</Button>
                  )}
                  {canAct && (
                    <Button
                      size="xs"
                      colorScheme="orange"
                      variant="outline"
                      leftIcon={<Icon as={MdPersonOff} w="11px" h="11px" />}
                      onClick={() => setNoShowConfirm(true)}
                    >
                      No-Show
                    </Button>
                  )}
                  {canAct && (
                    <Button size="xs" colorScheme="red" variant="outline" onClick={onCancel}>Cancelar</Button>
                  )}
                </>
              ) : (
                <>
                  <Text fontSize="xs" color="orange.600" fontWeight="600" w="full">
                    ¿Registrar no-show para esta cita?
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="orange"
                    isLoading={noShowMut.isPending}
                    onClick={handleNoShow}
                  >
                    Sí, confirmar
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setNoShowConfirm(false)}>
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </Portal>
    </>
  );
}

// ─── Availability Edit Popover ─────────────────────────────────────────────────
const toHHMM = (t: string) => t.slice(0, 5);
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2).toString().padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

interface AvailPeriod { startTime: string; endTime: string; }

function buildSlotInputs(
  availability: Availability[],
  dayOfWeek: string,
  newPeriods: AvailPeriod[],
): { dayOfWeek: string; startTime: string; endTime: string; slotDurationMinutes: number; zonaHoraria: string }[] {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Guayaquil';

  const otherDays = availability.filter(a => a.dayOfWeek !== dayOfWeek && a.isActive);
  const kept = otherDays.map(a => ({
    dayOfWeek: a.dayOfWeek,
    startTime: toHHMM(a.startTime),
    endTime: toHHMM(a.endTime),
    slotDurationMinutes: a.slotDurationMinutes,
    zonaHoraria: a.zonaHoraria || tz,
  }));

  const edited = newPeriods.map(p => ({
    dayOfWeek,
    startTime: p.startTime,
    endTime: p.endTime,
    slotDurationMinutes: timeMins(p.endTime) - timeMins(p.startTime),
    zonaHoraria: tz,
  }));

  return [...kept, ...edited];
}

interface AvailPopoverProps {
  dayOfWeek: string;
  pos: { x: number; y: number };
  availability: Availability[];
  professionalId: string;
  onClose: () => void;
  initialPeriod?: AvailPeriod;
}

function AvailabilityEditPopover({ dayOfWeek, pos, availability, professionalId, onClose, initialPeriod }: AvailPopoverProps) {
  const ref   = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const batchMut = useBatchReplaceAvailability(professionalId);

  const dayLabel = DAY_FULL_ES[DAY_TO_WEEKDAY[dayOfWeek] ?? 0];

  const initialPeriods: AvailPeriod[] = availability
    .filter(a => a.dayOfWeek === dayOfWeek && a.isActive)
    .map(a => ({ startTime: toHHMM(a.startTime), endTime: toHHMM(a.endTime) }))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  if (initialPeriod && !initialPeriods.some(p => p.startTime === initialPeriod.startTime)) {
    initialPeriods.push(initialPeriod);
    initialPeriods.sort((a, b) => a.startTime.localeCompare(b.startTime));
  }

  const [periods, setPeriods] = useState<AvailPeriod[]>(
    initialPeriods.length > 0 ? initialPeriods : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ left: pos.x + 12, top: pos.y });

  useEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect   = ref.current?.getBoundingClientRect();
    const width  = rect?.width  || 300;
    const height = rect?.height || 200;
    let x = pos.x + 12;
    let y = pos.y - 20;
    if (x + width + 12 > vw) x = vw - width - 12;
    if (x < 8) x = 8;
    if (y + height + 12 > vh) y = vh - height - 12;
    if (y < 8) y = 8;
    setStyle({ left: x, top: y });
  }, [pos]);

  const updatePeriod = (idx: number, field: 'startTime' | 'endTime', value: string) =>
    setPeriods(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));

  const addPeriod = () =>
    setPeriods(prev => [...prev, { startTime: '09:00', endTime: '17:00' }]);

  const removePeriod = (idx: number) =>
    setPeriods(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setError(null);
    for (const p of periods) {
      if (p.startTime >= p.endTime) {
        setError('La hora de inicio debe ser anterior a la hora fin.');
        return;
      }
    }
    const slots = buildSlotInputs(availability, dayOfWeek, periods);
    try {
      await batchMut.mutateAsync({ slots });
      toast({ title: 'Disponibilidad actualizada', status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Error al guardar.'));
    }
  };

  return (
    <>
      <div className="ag-backdrop" onClick={onClose} />
      <Portal>
        <div ref={ref} className="ag-popup" style={{ ...style, width: 340 }}>
          <div className="ag-popup-header">
            <p className="ag-popup-title">Disponibilidad — {dayLabel}</p>
            <button className="ag-popup-close" onClick={onClose} aria-label="Cerrar">
              <Icon as={MdClose} w="16px" h="16px" />
            </button>
          </div>

          <div className="ag-popup-body">
            {periods.length === 0 ? (
              <Text fontSize="sm" color="gray.500">Día no disponible.</Text>
            ) : (
              periods.map((p, idx) => (
                <Flex key={idx} align="center" gap="6px">
                  <TimeCombobox value={p.startTime} onChange={v => updatePeriod(idx, 'startTime', v)} options={TIME_OPTIONS} listMaxH="160px" flex={1} w="0" />
                  <Text fontSize="sm" color="gray.500" flexShrink={0}>–</Text>
                  <TimeCombobox value={p.endTime} onChange={v => updatePeriod(idx, 'endTime', v)} options={TIME_OPTIONS} listMaxH="160px" flex={1} w="0" />
                  <button
                    onClick={() => removePeriod(idx)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', flexShrink: 0 }}
                    title="Quitar franja"
                  >
                    <Icon as={MdClose} w="14px" h="14px" color="gray.400" />
                  </button>
                </Flex>
              ))
            )}

            <button
              onClick={addPeriod}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: '#6366F1', fontSize: 12, fontWeight: 600, padding: 0 }}
            >
              <Icon as={MdAdd} w="14px" h="14px" /> {periods.length === 0 ? 'Activar día' : 'Añadir franja'}
            </button>

            {error && <Text fontSize="xs" color="red.500">{error}</Text>}
          </div>

          <div className="ag-popup-actions">
            <Button
              size="xs"
              colorScheme="brand"
              leftIcon={<Icon as={MdSave} w="11px" h="11px" />}
              isLoading={batchMut.isPending}
              onClick={handleSave}
            >
              Guardar
            </Button>
            <Button size="xs" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </div>
      </Portal>
    </>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────
export default function AppointmentsView() {
  const { user }                    = useUser();
  const role                        = getUserRole(user) ?? '';
  const canWrite                    = ['admin', 'medico', 'fisioterapeuta'].includes(role);
  const isAdmin                     = role === 'admin';
  const { data: currentDbUser }     = useCurrentDbUser();
  const toast                       = useToast();
  const rescheduleMut               = useRescheduleAppointment();
  const capacidadMut                = useUpdateCapacidad();

  // ── View & navigation ────────────────────────────────────────────────────────
  const [viewMode,    setViewMode]    = useState<ViewMode>('week');
  const [weekStart,   setWeekStart]   = useState<Date>(() => getMonday(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date>(() => new Date());

  const weekDays = React.useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Days shown in grid (week: 7, day: 1, month: grid handles its own)
  const activeDays = React.useMemo(() => {
    if (viewMode === 'day')   return [selectedDay];
    if (viewMode === 'month') return [] as Date[];
    return weekDays;
  }, [viewMode, selectedDay, weekDays]);

  const prevPeriod = () => {
    if (viewMode === 'week')       setWeekStart(d => addDays(d, -7));
    else if (viewMode === 'day')   setSelectedDay(d => addDays(d, -1));
    else setSelectedDay(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };
  const nextPeriod = () => {
    if (viewMode === 'week')       setWeekStart(d => addDays(d, 7));
    else if (viewMode === 'day')   setSelectedDay(d => addDays(d, 1));
    else setSelectedDay(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  };
  const goToday = () => {
    const today = new Date();
    setWeekStart(getMonday(today));
    setSelectedDay(today);
  };
  const switchView = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'day' && viewMode === 'week') setSelectedDay(weekStart);
  };

  // ── Nav label ────────────────────────────────────────────────────────────────
  const navLabel = React.useMemo(() => {
    if (viewMode === 'week') {
      const startM = weekStart.getMonth();
      const endM   = weekDays[6].getMonth();
      const yr     = weekStart.getFullYear();
      return startM === endM
        ? `${MONTH_ES[startM]} ${yr}`
        : `${MONTH_ES[startM]} / ${MONTH_ES[endM]} ${yr}`;
    }
    if (viewMode === 'day') {
      const d = selectedDay;
      return `${DAY_FULL_ES[d.getDay()]}, ${d.getDate()} de ${MONTH_ES[d.getMonth()]} ${d.getFullYear()}`;
    }
    return `${MONTH_ES[selectedDay.getMonth()]} ${selectedDay.getFullYear()}`;
  }, [viewMode, weekStart, weekDays, selectedDay]);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [showFilters,    setShowFilters]    = useState(false);
  const [filterTipoCita, setFilterTipoCita] = useState('');
  const [filterEstado,   setFilterEstado]   = useState('');
  const hasActiveFilters = !!(filterTipoCita || filterEstado);

  // ── DnD ──────────────────────────────────────────────────────────────────────
  const [dragAppt,   setDragAppt]   = useState<Appointment | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dragAvailGap, setDragAvailGap] = useState<{
    apptId: string; professionalId: string; scheduledAt: string;
  } | null>(null);
  const [dragAvailDuration, setDragAvailDuration] = useState(30);
  const createAvailMut = useCreateAvailability();

  const handleDragStart = useCallback((e: React.DragEvent, appt: Appointment) => {
    setDragAppt(appt);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appt.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragAppt(null);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, dateKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(dateKey);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, dateKey: string) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragAppt) return;

    const rect      = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const relY      = e.clientY - rect.top;
    const totalMins = (relY / HOUR_PX) * 60 + START_HOUR * 60;
    const rounded   = Math.round(totalMins / 30) * 30;
    const clamped   = Math.min(Math.max(rounded, START_HOUR * 60), (END_HOUR - 1) * 60);
    const h         = Math.floor(clamped / 60);
    const m         = clamped % 60;
    const scheduledAt = new Date(`${dateKey}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`).toISOString();

    try {
      await rescheduleMut.mutateAsync({
        id: dragAppt.id,
        payload: { scheduledAt, motivo: 'Reagendado desde calendario' },
      });
      toast({ title: 'Cita reagendada', status: 'success', duration: 3000, isClosable: true });
    } catch (err: any) {
      const status: number | undefined = err?.response?.status;
      const message: string = err?.response?.data?.message ?? '';
      if (status === 422 && message.includes('no tiene disponibilidad configurada')) {
        setDragAvailGap({ apptId: dragAppt.id, professionalId: dragAppt.professionalId, scheduledAt });
      } else {
        toast({ title: 'Error al reagendar', description: message || 'No se pudo reagendar', status: 'error', duration: 6000, isClosable: true });
      }
    }
    setDragAppt(null);
  }, [dragAppt, rescheduleMut, toast]);

  const handleConfigureDragAvailability = async () => {
    if (!dragAvailGap) return;
    const d = new Date(dragAvailGap.scheduledAt);
    const pad = (n: number) => String(n).padStart(2, '0');
    const startTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const endDate = new Date(d.getTime() + dragAvailDuration * 60_000);
    const endTime = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
    const DOW = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    let existingAvailOverlaps = false;

    try {
      await createAvailMut.mutateAsync({
        professionalId: dragAvailGap.professionalId,
        dayOfWeek: DOW[d.getDay()],
        startTime,
        endTime,
        slotDurationMinutes: dragAvailDuration,

      });
    } catch (createErr: any) {
      const createMsg: string = createErr?.response?.data?.message ?? '';
      if (createMsg.includes('ya tiene disponibilidad')) {
        existingAvailOverlaps = true;
      } else {
        toast({ title: 'No se pudo configurar la disponibilidad', description: createMsg || 'Error inesperado', status: 'error', duration: 4000, isClosable: true });
        return;
      }
    }

    try {
      await rescheduleMut.mutateAsync({
        id: dragAvailGap.apptId,
        payload: { scheduledAt: dragAvailGap.scheduledAt, motivo: 'Reagendado desde calendario' },
      });
      toast({ title: 'Cita reagendada', status: 'success', duration: 3000, isClosable: true });
      setDragAvailGap(null);
    } catch (reschedErr: any) {
      const reschedMsg: string = reschedErr?.response?.data?.message ?? '';
      const isNoAvail = reschedMsg.includes('no tiene disponibilidad');
      if (existingAvailOverlaps && isNoAvail) {
        toast({
          title: 'Franja existente no cubre este horario',
          description: `Ya tienes disponibilidad configurada para ese día, pero no cubre las ${startTime}. Edita tu franja en Perfil → Disponibilidad.`,
          status: 'warning', duration: 8000, isClosable: true,
        });
        setDragAvailGap(null);
      } else {
        toast({ title: 'No se pudo completar', description: reschedMsg || 'Error inesperado', status: 'error', duration: 5000, isClosable: true });
      }
    }
  };

  // ── Data fetching ────────────────────────────────────────────────────────────
  const queryDates = React.useMemo(() => {
    if (viewMode === 'week') {
      return { desde: toApiDate(weekStart), hasta: toApiDate(weekDays[6]) };
    }
    if (viewMode === 'day') {
      return { desde: toApiDate(selectedDay), hasta: toApiDate(selectedDay) };
    }
    const y = selectedDay.getFullYear();
    const mo = selectedDay.getMonth();
    return {
      desde: toApiDate(new Date(y, mo, 1)),
      hasta: toApiDate(new Date(y, mo + 1, 0)),
    };
  }, [viewMode, weekStart, weekDays, selectedDay]);

  const { data: result, isLoading, isFetching, isError: apptError, refetch: refetchAppts } = useAppointments({
    ...queryDates,
    limit: 500,
  });

  const { data: availability = [] } = useProfessionalAvailability(currentDbUser?.id);
  const capacity = currentDbUser?.capacidadAtencionParalela ?? 1;

  const allAppts = React.useMemo(() => result?.data ?? [], [result]);

  const filteredAppts = React.useMemo(() => {
    return allAppts.filter(a => {
      if (!filterEstado && a.estado === EstadoCita.REPROGRAMADA) return false;
      if (filterTipoCita && a.tipoCita !== filterTipoCita) return false;
      if (filterEstado   && a.estado   !== filterEstado)   return false;
      return true;
    });
  }, [allAppts, filterTipoCita, filterEstado]);

  const byDay = React.useMemo<Record<string, Appointment[]>>(() => {
    const map: Record<string, Appointment[]> = {};
    for (const a of filteredAppts) {
      const key = toDateKey(new Date(a.scheduledAt));
      if (!map[key]) map[key] = [];
      map[key].push(a);
    }
    return map;
  }, [filteredAppts]);

  const slotsByDay = React.useMemo(
    () => viewMode === 'month'
      ? {}
      : generateSlotBands(availability, allAppts, activeDays, capacity, START_HOUR, HOUR_PX, GRID_HEIGHT),
    [viewMode, availability, allAppts, activeDays, capacity],
  );

  // ── Misc ─────────────────────────────────────────────────────────────────────
  const [slotInitial, setSlotInitial] = useState<{ scheduledAt: string; dur: number } | null>(null);
  const [hoverSlot,   setHoverSlot]   = useState<{ key: string; top: number; label: string } | null>(null);
  const [timeTop,     setTimeTop]     = useState(nowTopPx);
  useEffect(() => {
    const id = setInterval(() => setTimeTop(nowTopPx()), 60_000);
    return () => clearInterval(id);
  }, []);

  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = (7 - START_HOUR) * HOUR_PX;
  }, [viewMode, isLoading]);

  const [popup,          setPopup]          = useState<{ appt: Appointment; pos: { x: number; y: number } } | null>(null);
  const [availPopup,     setAvailPopup]     = useState<{ dayOfWeek: string; pos: { x: number; y: number }; initialPeriod?: { startTime: string; endTime: string } } | null>(null);
  const closePopup                          = () => setPopup(null);
  const { isOpen: isDrawerOpen,     onOpen: onDrawerOpen,     onClose: onDrawerClose     } = useDisclosure();
  const { isOpen: isPlanDrawerOpen, onOpen: onPlanDrawerOpen, onClose: onPlanDrawerClose } = useDisclosure();
  const { isOpen: isAvailDrawerOpen, onOpen: onAvailDrawerOpen, onClose: onAvailDrawerClose } = useDisclosure();
  const [editTarget,      setEditTarget]      = useState<Appointment | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<Appointment | null>(null);
  const [cancelTarget,   setCancelTarget]   = useState<Appointment | null>(null);
  const [completeTarget, setCompleteTarget] = useState<Appointment | null>(null);
  const [noAvailConfirm, setNoAvailConfirm] = useState<{ dayOfWeek: string; pos: { x: number; y: number }; period: { startTime: string; endTime: string } } | null>(null);

  const hasAvailabilityAt = (date: Date, mins: number) => {
    const dayOfWeek = WEEKDAY_TO_DAY[date.getDay()];
    return availability.some(a => {
      if (!a.isActive || a.dayOfWeek !== dayOfWeek) return false;
      const start = timeMins(toHHMM(a.startTime));
      const end   = timeMins(toHHMM(a.endTime));
      return mins >= start && mins < end;
    });
  };

  const hasDayAvailability = (date: Date) => {
    const dayOfWeek = WEEKDAY_TO_DAY[date.getDay()];
    return availability.some(a => a.isActive && a.dayOfWeek === dayOfWeek);
  };

  const openEdit        = (a: Appointment) => { closePopup(); setEditTarget(a);        };
  const openReschedule  = (a: Appointment) => { closePopup(); setRescheduleTarget(a);  };
  const openCancel   = (a: Appointment) => { closePopup(); setCancelTarget(a);   };
  const openComplete = (a: Appointment) => { closePopup(); setCompleteTarget(a); };

  const handleSlotClick = (band: SlotBand) => {
    if (!canWrite || band.status === 'full' || band.status === 'past') return;
    setSlotInitial({ scheduledAt: band.isoStart, dur: band.durationMinutes });
    onDrawerOpen();
  };
  const handleDrawerClose = () => { onDrawerClose(); setSlotInitial(null); };

  const todayKey = toDateKey(new Date());

  // ── Chakra ───────────────────────────────────────────────────────────────────
  const cardBg      = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const headerBg    = useColorModeValue('gray.50', 'navy.700');
  const textColor   = useColorModeValue('secondaryGray.900', 'white');

  // ── Preview band: muestra de fondo la franja que se está agregando ──────────
  const previewPeriod = noAvailConfirm?.period ?? availPopup?.initialPeriod ?? null;
  const previewDayOfWeek = noAvailConfirm?.dayOfWeek ?? availPopup?.dayOfWeek ?? null;
  const previewDay = previewDayOfWeek
    ? weekDays.find(d => WEEKDAY_TO_DAY[d.getDay()] === previewDayOfWeek)
    : null;
  const previewBand = previewPeriod && previewDay
    ? {
        key: toDateKey(previewDay),
        top: (timeMins(previewPeriod.startTime) / 60 - START_HOUR) * HOUR_PX,
        height: Math.max(22, (timeMins(previewPeriod.endTime) - timeMins(previewPeriod.startTime)) / 60 * HOUR_PX),
        label: `${previewPeriod.startTime} – ${previewPeriod.endTime}`,
      }
    : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // Grid view (week / day)
  // ─────────────────────────────────────────────────────────────────────────────
  const renderGridView = () => (
    <Box position="relative" bg={cardBg} borderRadius="16px" border="1px solid" borderColor={borderColor} overflow="hidden" boxShadow="0 2px 12px rgba(0,0,0,0.06)">
      {isFetching && !isLoading && (
        <Progress isIndeterminate size="xs" colorScheme="brand" position="absolute" top="0" left="0" right="0" zIndex="10" borderRadius="0" bg="transparent" />
      )}
      <Box bg={headerBg} overflowX="auto">
        <div className="ag-day-headers">
          <div className="ag-day-header-spacer" />
          {activeDays.map(day => {
            const key     = toDateKey(day);
            const isToday = key === todayKey;
            // getDay(): 0=Sun…6=Sat → index into DAY_ABBR_ES (Mon=0…Sun=6)
            const abbr    = DAY_ABBR_ES[day.getDay() === 0 ? 6 : day.getDay() - 1];
            return (
              <div key={key} className="ag-day-header-cell">
                <span className={`ag-day-abbr${isToday ? ' ag-day-abbr--today' : ''}`}>{abbr}</span>
                <span className={`ag-day-num${isToday ? ' ag-day-num--today' : ''}`}>{day.getDate()}</span>
              </div>
            );
          })}
        </div>
      </Box>

      <div className="ag-grid-scroll" ref={gridRef}>
        {isLoading ? (
          <Flex h="400px" px="16px" gap="8px" py="12px">
            <Box w="56px" flexShrink={0} />
            {activeDays.map((_, i) => (
              <div key={i} className="ag-skeleton-col">
                {i < 5 && (
                  <>
                    <Skeleton h="52px" borderRadius="8px" opacity={0.6} />
                    {i % 2 === 0 && <Skeleton h="36px" borderRadius="8px" opacity={0.4} mt="48px" />}
                  </>
                )}
              </div>
            ))}
          </Flex>
        ) : (
          <div className="ag-grid-body">
            <div className="ag-time-col" style={{ height: GRID_HEIGHT }}>
              {HOURS.map(h => (
                <div key={h} className="ag-time-label" style={{ top: (h - START_HOUR) * HOUR_PX }}>
                  {h}:00
                </div>
              ))}
            </div>

            <div className="ag-days-area">
              {activeDays.map(day => {
                const key          = toDateKey(day);
                const isToday      = key === todayKey;
                const appts        = byDay[key] ?? [];
                const apptCols     = computeApptColumns(appts, slotsByDay[key] ?? []);
                const isDropTarget = dropTarget === key;

                return (
                  <div
                    key={key}
                    className={`ag-day-col${isToday ? ' ag-day-col--today' : ''}${isDropTarget ? ' ag-day-col--drop-target' : ''}`}
                    style={{ height: GRID_HEIGHT }}
                    onDragOver={dragAppt ? e => handleDragOver(e, key) : undefined}
                    onDragLeave={dragAppt ? handleDragLeave : undefined}
                    onDrop={dragAppt ? e => handleDrop(e, key) : undefined}
                    onMouseMove={e => {
                      if (!canWrite || dragAppt) return;
                      const target = e.target as HTMLElement;
                      if (target.closest('.ag-appt') || target.closest('.ag-slot-band')) { setHoverSlot(null); return; }
                      const rect    = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      const relY    = e.clientY - rect.top;
                      const rawMins = Math.round((relY / HOUR_PX) * 60 / 15) * 15 + START_HOUR * 60;
                      const clamped = Math.min(Math.max(rawMins, START_HOUR * 60), (END_HOUR - 1) * 60);
                      const top     = (clamped / 60 - START_HOUR) * HOUR_PX;
                      const label   = `${minsToTime(clamped)} – ${minsToTime(Math.min(clamped + 60, END_HOUR * 60))}`;
                      setHoverSlot({ key, top, label });
                    }}
                    onMouseLeave={() => setHoverSlot(null)}
                    onDoubleClick={e => {
                      if (!canWrite) return;
                      const target = e.target as HTMLElement;
                      if (target.closest('.ag-appt') || target.closest('.ag-slot-band')) return;
                      const rect     = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      const relY     = e.clientY - rect.top;
                      const rawMins  = Math.round((relY / HOUR_PX) * 60 / 15) * 15 + START_HOUR * 60;
                      const clamped  = Math.min(Math.max(rawMins, START_HOUR * 60), (END_HOUR - 1) * 60);
                      const h        = Math.floor(clamped / 60);
                      const m        = clamped % 60;
                      if (!hasAvailabilityAt(day, clamped)) {
                        const startTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                        const endTime   = minsToTime(Math.min(clamped + 60, END_HOUR * 60));
                        const slotTop   = (clamped / 60 - START_HOUR) * HOUR_PX;
                        const dow       = WEEKDAY_TO_DAY[day.getDay()];
                        const pos       = { x: rect.right + 16, y: rect.top + slotTop };
                        if (hasDayAvailability(day)) {
                          // Day has availability, just not at this time — add new band directly
                          setAvailPopup({ dayOfWeek: dow, pos, initialPeriod: { startTime, endTime } });
                        } else {
                          setNoAvailConfirm({ dayOfWeek: dow, pos, period: { startTime, endTime } });
                        }
                        return;
                      }
                      const iso      = `${key}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                      setSlotInitial({ scheduledAt: iso, dur: 30 });
                      onDrawerOpen();
                    }}
                  >
                    {HOURS.map(h => (
                      <div key={h} className="ag-hour-line" style={{ top: (h - START_HOUR) * HOUR_PX }} />
                    ))}
                    {HOURS.slice(0, -1).map(h => (
                      <div key={`${h}-half`} className="ag-hour-line ag-hour-line--half" style={{ top: (h - START_HOUR) * HOUR_PX + HOUR_PX / 2 }} />
                    ))}

                    {hoverSlot?.key === key && (
                      <div className="ag-hover-slot" style={{ top: hoverSlot.top, height: HOUR_PX }}>
                        <span className="ag-hover-slot-label">{hoverSlot.label}</span>
                      </div>
                    )}

                    {previewBand?.key === key && (
                      <div className="ag-preview-slot" style={{ top: previewBand.top, height: previewBand.height }}>
                        <span className="ag-hover-slot-label">{previewBand.label}</span>
                      </div>
                    )}

                    {(slotsByDay[key] ?? []).map((band, bi) => (
                      <div
                        key={`slot-${bi}`}
                        className={`ag-slot-band ag-slot-band--${band.status}`}
                        style={{ top: band.topPx, height: band.heightPx }}
                        onClick={e => { e.stopPropagation(); handleSlotClick(band); }}
                        onContextMenu={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!canWrite) return;
                          setAvailPopup({ dayOfWeek: band.dayOfWeek, pos: { x: e.clientX, y: e.clientY } });
                        }}
                        title={band.status === 'full' ? 'Slot lleno' : band.status === 'past' ? '' : `Clic para agendar — ${band.startTime} · clic derecho para editar disponibilidad`}
                      >
                        {band.status === 'empty' && band.heightPx >= 28 && (
                          <span className="ag-slot-add-hint">+</span>
                        )}
                        {(band.status === 'partial' || band.status === 'full') && (
                          <span className="ag-slot-cap">{band.count}/{band.capacity}</span>
                        )}
                      </div>
                    ))}

                    {isToday && timeTop >= 0 && timeTop <= GRID_HEIGHT && (
                      <div className="ag-now-line" style={{ top: timeTop }} />
                    )}

                    {appts.map(appt => {
                      const style      = ESTADO_STYLE[appt.estado];
                      const top        = getTopPx(appt.scheduledAt);
                      const height     = getHeightPx(appt.durationMinutes);
                      const faded      = FADED_ESTADOS.has(appt.estado);
                      const tall       = height >= 36;
                      const isDraggable = canWrite && appt.estado === EstadoCita.CONFIRMADA;
                      const isDragging  = dragAppt?.id === appt.id;
                      const { col, total } = apptCols[appt.id] ?? { col: 0, total: 1 };
                      const colWidth    = 100 / total;

                      return (
                        <div
                          key={appt.id}
                          className={`ag-appt${faded ? ' ag-appt--faded' : ''}${isDraggable ? ' ag-appt--draggable' : ''}${isDragging ? ' ag-appt--dragging' : ''}`}
                          style={{
                            top, height, background: style.bg, color: style.text,
                            ...(total > 1
                              ? { left: `calc(${col * colWidth}% + 3px)`, right: 'auto', width: `calc(${colWidth}% - 6px)` }
                              : {}),
                          }}
                          draggable={isDraggable}
                          onDragStart={isDraggable ? e => handleDragStart(e, appt) : undefined}
                          onDragEnd={isDraggable ? handleDragEnd : undefined}
                          onClick={e => {
                            e.stopPropagation();
                            if (!isDragging) setPopup({ appt, pos: { x: e.clientX, y: e.clientY } });
                          }}
                        >
                          {tall && (
                            <div className="ag-appt-patient">
                              {appt.patient.nombres} {appt.patient.apellidos}
                            </div>
                          )}
                          <div className="ag-appt-time">
                            {fmtTime(appt.scheduledAt)}
                            {tall && ` – ${fmtEndTime(appt.scheduledAt, appt.durationMinutes)}`}
                          </div>
                          {tall && appt.treatmentPlanId && appt.sessionInfo && (
                            <div className="ag-appt-tag ag-appt-tag--plan">
                              Plan: Ses. {appt.sessionInfo.numeroSesion}/{appt.sessionInfo.totalSessions}
                            </div>
                          )}
                          {tall && appt.treatmentPlanId && !appt.sessionInfo && (
                            <div className="ag-appt-tag ag-appt-tag--plan">Plan</div>
                          )}
                          {tall && !appt.treatmentPlanId && (
                            <div className="ag-appt-tag ag-appt-tag--libre">Consulta Libre</div>
                          )}
                          {isDraggable && tall && (
                            <div className="ag-appt-drag-handle" title="Arrastrar para reagendar">⠿</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Month view
  // ─────────────────────────────────────────────────────────────────────────────
  const renderMonthView = () => {
    const y          = selectedDay.getFullYear();
    const mo         = selectedDay.getMonth();
    const firstOfMo  = new Date(y, mo, 1);
    const gridStart  = getMonday(firstOfMo);
    const cells      = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

    const monthByDay: Record<string, Appointment[]> = {};
    for (const a of filteredAppts) {
      const key = toDateKey(new Date(a.scheduledAt));
      if (!monthByDay[key]) monthByDay[key] = [];
      monthByDay[key].push(a);
    }

    return (
      <Box position="relative" bg={cardBg} borderRadius="16px" border="1px solid" borderColor={borderColor} overflow="hidden" boxShadow="0 2px 12px rgba(0,0,0,0.06)">
        {isFetching && !isLoading && (
          <Progress isIndeterminate size="xs" colorScheme="brand" position="absolute" top="0" left="0" right="0" zIndex="10" borderRadius="0" bg="transparent" />
        )}
        {isLoading ? (
          <Flex h="400px" align="center" justify="center" p="24px">
            <Skeleton h="100%" w="100%" borderRadius="8px" />
          </Flex>
        ) : (
          <div className="ag-month-grid">
            <div className="ag-month-weekdays">
              {DAY_ABBR_ES.map(d => (
                <div key={d} className="ag-month-weekday-label">{d}</div>
              ))}
            </div>
            <div className="ag-month-cells">
              {cells.map(day => {
                const key         = toDateKey(day);
                const isThisMonth = day.getMonth() === mo;
                const isToday     = key === todayKey;
                const dayAppts    = monthByDay[key] ?? [];
                const dots        = dayAppts.slice(0, 3);
                const extra       = dayAppts.length - 3;

                return (
                  <div
                    key={key}
                    className={`ag-month-day${!isThisMonth ? ' ag-month-day--other' : ''}${isToday ? ' ag-month-day--today' : ''}`}
                    onClick={() => { setSelectedDay(day); switchView('day'); }}
                  >
                    <span className={`ag-month-day-num${isToday ? ' ag-month-day-num--today' : ''}`}>
                      {day.getDate()}
                    </span>
                    <div className="ag-month-dots">
                      {dots.map(a => (
                        <span
                          key={a.id}
                          className="ag-month-dot"
                          style={{ background: ESTADO_STYLE[a.estado].bg }}
                          title={`${a.patient.nombres} ${a.patient.apellidos} — ${fmtTime(a.scheduledAt)}`}
                        />
                      ))}
                      {extra > 0 && (
                        <span className="ag-month-extra">+{extra}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Box>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box pt="80px" px={{ base: '12px', md: '24px' }} w="100%">

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <Flex align="center" justify="space-between" mb="16px" flexWrap="wrap" gap="12px">
        <Flex align="center" gap="8px">
          <Tooltip label="Hoy" hasArrow>
            <Button size="sm" variant="outline" onClick={goToday} leftIcon={<Icon as={MdToday} />}>
              Hoy
            </Button>
          </Tooltip>
          <Flex gap="4px">
            <IconButton aria-label="Período anterior"   icon={<Icon as={MdChevronLeft}  />} size="sm" variant="ghost" onClick={prevPeriod} />
            <IconButton aria-label="Período siguiente" icon={<Icon as={MdChevronRight} />} size="sm" variant="ghost" onClick={nextPeriod} />
          </Flex>
          <Text fontSize="lg" fontWeight="700" color={textColor} minW="220px">{navLabel}</Text>
        </Flex>

        <Flex align="center" gap="10px" flexWrap="wrap">
          {/* View switcher */}
          <div className="ag-view-switcher">
            <button
              className={`ag-view-btn${viewMode === 'month' ? ' ag-view-btn--active' : ''}`}
              onClick={() => switchView('month')}
            >
              <Icon as={MdViewModule} w="15px" h="15px" />
              <span>Mes</span>
            </button>
            <button
              className={`ag-view-btn${viewMode === 'week' ? ' ag-view-btn--active' : ''}`}
              onClick={() => switchView('week')}
            >
              <Icon as={MdViewWeek} w="15px" h="15px" />
              <span>Semana</span>
            </button>
            <button
              className={`ag-view-btn${viewMode === 'day' ? ' ag-view-btn--active' : ''}`}
              onClick={() => switchView('day')}
            >
              <Icon as={MdViewDay} w="15px" h="15px" />
              <span>Día</span>
            </button>
          </div>

          {/* Filter toggle */}
          <Tooltip label="Filtros avanzados" hasArrow>
            <IconButton
              aria-label="Filtros"
              icon={<Icon as={MdFilterList} />}
              size="sm"
              variant={showFilters || hasActiveFilters ? 'solid' : 'ghost'}
              colorScheme={showFilters || hasActiveFilters ? 'brand' : 'gray'}
              onClick={() => setShowFilters(v => !v)}
            />
          </Tooltip>

          {canWrite && (
            <Flex gap="8px" align="center">
              <Flex align="center" gap="6px">
                <Text fontSize="xs" fontWeight="600" color={textColor} opacity={0.6} whiteSpace="nowrap">
                  Pac./sesión:
                </Text>
                <Dropdown
                  label={capacity === 1 ? '1 pac.' : `${capacity} pac.`}
                  items={[
                    { label: '1 paciente',  value: 1 },
                    { label: '2 pacientes', value: 2 },
                    { label: '3 pacientes', value: 3 },
                    { label: '4 pacientes', value: 4 },
                    { label: '5 pacientes', value: 5 },
                  ]}
                  onSelect={async (v) => {
                    if (!currentDbUser?.id) return;
                    try {
                      await capacidadMut.mutateAsync({ id: currentDbUser.id, capacidad: Number(v) });
                      toast({ title: `Capacidad actualizada a ${v} pac./sesión`, status: 'success', duration: 2000, isClosable: true, position: 'top-right' });
                    } catch {
                      toast({ title: 'Error al actualizar capacidad', status: 'error', duration: 3000, isClosable: true, position: 'top-right' });
                    }
                  }}
                  variant="outline"
                  size="sm"
                  w="110px"
                />
              </Flex>
              <Button
                leftIcon={<Icon as={MdEventAvailable} />}
                size="sm"
                variant="outline"
                onClick={onAvailDrawerOpen}
              >
                Ajuste de Agenda
              </Button>
              <Button
                leftIcon={<Icon as={MdEventNote} />}
                colorScheme="teal"
                size="sm"
                variant="outline"
                onClick={onPlanDrawerOpen}
              >
                Nuevo Plan
              </Button>
              <Button
                leftIcon={<Icon as={MdAdd} />}
                colorScheme="brand"
                size="sm"
                onClick={() => { setSlotInitial(null); onDrawerOpen(); }}
              >
                Agendar cita
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* ── Filter panel ─────────────────────────────────────────────────────── */}
      <Collapse in={showFilters} animateOpacity>
        <div className="ag-filter-panel">
          <div className="ag-filter-row">
            <span className="ag-filter-label">Tipo de cita</span>
            <Select
              size="sm"
              value={filterTipoCita}
              onChange={e => setFilterTipoCita(e.target.value)}
              placeholder="Todos"
              maxW="200px"
              borderRadius="8px"
            >
              {Object.values(TipoCita).map(t => (
                <option key={t} value={t}>{TIPO_LABEL[t as TipoCita]}</option>
              ))}
            </Select>
          </div>
          <div className="ag-filter-row">
            <span className="ag-filter-label">Estado</span>
            <Select
              size="sm"
              value={filterEstado}
              onChange={e => setFilterEstado(e.target.value)}
              placeholder="Todos"
              maxW="200px"
              borderRadius="8px"
            >
              {Object.values(EstadoCita).map(est => (
                <option key={est} value={est}>{ESTADO_LABEL[est as EstadoCita]}</option>
              ))}
            </Select>
          </div>
          {hasActiveFilters && (
            <button
              className="ag-filter-clear"
              onClick={() => { setFilterTipoCita(''); setFilterEstado(''); }}
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </Collapse>

      {/* ── Calendar body ────────────────────────────────────────────────────── */}
      {apptError && (
        <Box px='16px' py='8px'>
          <Text fontSize='xs' color='red.500' fontWeight='600'>
            Error al cargar citas — revisa la consola del backend y recarga la página
          </Text>
        </Box>
      )}
      {viewMode === 'month' ? renderMonthView() : renderGridView()}

      {/* ── Popup ────────────────────────────────────────────────────────────── */}
      {popup && (
        <AppointmentPopup
          appt={popup.appt}
          pos={popup.pos}
          canWrite={canWrite}
          isAdmin={isAdmin}
          currentUserId={currentDbUser?.id}
          onClose={closePopup}
          onEdit={() => openEdit(popup.appt)}
          onCancel={() => openCancel(popup.appt)}
          onComplete={() => openComplete(popup.appt)}
          onReschedule={() => openReschedule(popup.appt)}
        />
      )}

      {rescheduleTarget && (
        <AppointmentRescheduleModal
          isOpen
          onClose={() => setRescheduleTarget(null)}
          appointment={rescheduleTarget}
        />
      )}

      {dragAvailGap && (
        <CustomModal
          isOpen
          onClose={() => setDragAvailGap(null)}
          title="Sin disponibilidad para ese horario"
          primaryBtnLabel="Configurar y reagendar"
          secondaryBtnLabel="Cancelar"
          primaryBtnLoading={createAvailMut.isPending || rescheduleMut.isPending}
          onPrimaryClick={handleConfigureDragAvailability}
        >
          <Text fontSize="sm" mb="14px">
            El profesional no tiene disponibilidad configurada para ese día/hora.
            Define la franja para poder reagendar ahí.
          </Text>
          <Box>
              <Text fontSize="xs" color="gray.500" mb="4px">Duración de sesión</Text>
              <Dropdown
                label={dragAvailDuration === 60 ? '1 h' : `${dragAvailDuration} min`}
                items={[
                  { label: '15 min', value: 15 },
                  { label: '30 min', value: 30 },
                  { label: '45 min', value: 45 },
                  { label: '1 h',    value: 60 },
                ]}
                onSelect={v => setDragAvailDuration(Number(v))}
                w="160px"
                variant="outline"
                size="sm"
              />
            </Box>
        </CustomModal>
      )}

      {noAvailConfirm && (
        <CustomModal
          isOpen
          onClose={() => setNoAvailConfirm(null)}
          title="Día sin disponibilidad"
          primaryBtnLabel="Sí, agregar disponibilidad"
          secondaryBtnLabel="Cancelar"
          onPrimaryClick={() => {
            const { dayOfWeek, pos, period } = noAvailConfirm;
            setNoAvailConfirm(null);
            setAvailPopup({ dayOfWeek, pos, initialPeriod: period });
          }}
        >
          <Text fontSize="sm">
            No tienes disponibilidad configurada para el día{' '}
            <strong>{DAY_FULL_ES[DAY_TO_WEEKDAY[noAvailConfirm.dayOfWeek] ?? 0]}</strong>.
            ¿Deseas agregar disponibilidad para ese día?
          </Text>
        </CustomModal>
      )}

      {availPopup && currentDbUser?.id && (
        <AvailabilityEditPopover
          dayOfWeek={availPopup.dayOfWeek}
          pos={availPopup.pos}
          availability={availability}
          professionalId={currentDbUser.id}
          initialPeriod={availPopup.initialPeriod}
          onClose={() => setAvailPopup(null)}
        />
      )}

      {/* ── Modals / Drawer ──────────────────────────────────────────────────── */}
      <AppointmentDrawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        initialScheduledAt={slotInitial?.scheduledAt}
        professionalId={currentDbUser?.id ?? ''}
        onSuccess={() => { refetchAppts(); handleDrawerClose(); }}
      />
      {editTarget && (
        <AppointmentFormModal
          isOpen
          onClose={() => setEditTarget(null)}
          patientId={editTarget.patientId}
          appointment={editTarget}
          isAdmin={isAdmin}
        />
      )}
      {cancelTarget && (
        <AppointmentCancelModal
          isOpen
          onClose={() => setCancelTarget(null)}
          appointment={cancelTarget}
        />
      )}
      {completeTarget && (
        <AppointmentCompleteModal
          isOpen
          onClose={() => setCompleteTarget(null)}
          appointment={completeTarget}
        />
      )}
      <PlanScheduleDrawer
        isOpen={isPlanDrawerOpen}
        onClose={onPlanDrawerClose}
      />

      <CustomDrawer
        isOpen={isAvailDrawerOpen}
        onClose={onAvailDrawerClose}
        title="Ajustes de Agenda"
        size="md"
      >
        <AvailabilitySettingsCard boxShadow="none" p="0" />
      </CustomDrawer>
    </Box>
  );
}
